<template>
  <!-- CONFIRMED: replaces copy, JOIN and price line in place; CANCEL and the compliance strip are gone. -->
  <section class="confirmed" :class="`confirmed--${copy.style}`">
    <div class="confirmed__spacer" aria-hidden="true"></div>

    <div class="confirmed__block" role="status">
      <template v-if="copy.style === 'badge'">
        <span class="confirmed__badge" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
            <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <h1 class="confirmed__headline">{{ copy.headline }}</h1>
      </template>
      <template v-else>
        <p class="confirmed__eyebrow">{{ copy.eyebrow }}</p>
        <h1 class="confirmed__headline">{{ copy.line1 }}<span class="confirmed__accent">{{ copy.line2 }}</span></h1>
      </template>
      <p class="confirmed__body">{{ copy.body }}</p>
    </div>

    <!-- Profile CTA deep-links into the service; disabled until the brand's game URL is configured. -->
    <a v-if="gameHref" class="btn-join btn-join--cta" :href="gameHref" data-apex-cta="profile" :aria-busy="opening" @click="onProfile">
      <span v-if="opening" class="spinner" aria-hidden="true"></span>
      <span :class="{ 'visually-hidden': opening }">{{ copy.cta }}</span>
    </a>
    <button v-else class="btn-join btn-join--cta" type="button" disabled>{{ copy.cta }}</button>

    <div v-if="copy.style === 'badge'" class="confirmed__spacer" aria-hidden="true"></div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { formatMsisdn } from '@/functions/isMsisdnValid'
import resolveVariant from '@/functions/variant'

const { brandConfig, translations: t } = storeToRefs(useBrandStore())
const store = useSubscriptionStore()
const { msisdn, alreadySubscribed, portal } = storeToRefs(store)
const variant = brandConfig.value.variants[resolveVariant(brandConfig.value)]

// BLG gives each landing page its own confirmation lines; Rush and VS Gaming share one per service.
const copy = computed(() => {
  if (alreadySubscribed.value) return { ...t.value.welcomeBack, body: t.value.welcomeBackHint }
  return { ...t.value.confirmed, ...(variant.confirmed || {}) }
})

// Same link as Telkom's welcome SMS once the service has Telkom's result (/premium/result):
// ?result=ACTIVE&subscription_id=..&msisdn=..&ext_ref=.. plus ti (= ext_ref, Evina's transaction id):
// the VS Gaming portal signs the subscriber in only when msisdn, ti and subscription_id are all present.
// Until then the bare portal, plus the number when the page knows it (PIN flow).
const gameHref = computed(() => {
  const base = brandConfig.value.gameUrl
  if (!base) return ''
  const join = base.includes('?') ? '&' : '?'
  const telkom = portal.value
  if (telkom?.subscriptionId && telkom?.msisdn) {
    const params = new URLSearchParams({ result: telkom.result || 'ACTIVE', subscription_id: telkom.subscriptionId, msisdn: telkom.msisdn, ext_ref: telkom.extRef, ti: telkom.extRef })
    if (telkom.entryToken) params.set('et', telkom.entryToken)
    return `${base}${join}${params}`
  }
  const number = formatMsisdn(msisdn.value, brandConfig.value.msisdn)
  return number ? `${base}${join}msisdn=${number}` : base
})

onMounted(() => store.loadPortalResult(brandConfig.value))

// A tap before Telkom's result is in waits briefly for it, then opens whichever link it has.
const PORTAL_WAIT_MS = 4000
const opening = ref(false)
async function onProfile(event) {
  if (portal.value || opening.value) return
  event.preventDefault()
  opening.value = true
  await Promise.race([store.loadPortalResult(brandConfig.value), new Promise((resolve) => setTimeout(resolve, PORTAL_WAIT_MS))])
  window.location.assign(gameHref.value)
}
</script>

<style scoped>
.confirmed {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.confirmed__spacer {
  flex: 1;
  min-height: 24px;
}

.confirmed__block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.confirmed__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: var(--badge-fill);
  color: var(--badge-text);
}

.confirmed__eyebrow {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: var(--confirm-eyebrow-tracking);
  color: var(--color-primary);
}

.confirmed__headline {
  font-family: var(--headline-font);
  font-size: var(--confirm-headline-size);
  font-weight: 700;
  line-height: var(--confirm-headline-leading);
  letter-spacing: var(--headline-tracking);
  text-transform: var(--headline-transform);
  color: #fff;
}

.confirmed__accent {
  display: block;
  color: var(--color-primary);
}

.confirmed__body {
  max-width: 32ch;
  font-size: var(--confirm-body-size);
  font-weight: 400;
  line-height: 1.55;
  color: var(--confirm-body-color);
}

/* BLG's confirmation sits low over the art; the badge style is centred. */
.confirmed--split .confirmed__block {
  padding-bottom: 8px;
}
</style>
